import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../redux/storeProductSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../redux/storeCartSlice";
import { fetchAllCommercePartners } from "../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../redux/storeProductRatingSlice";
import { logout } from "../../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../redux/notificationsSlice";
import { CurrencyContext } from "../../../GLOBAL/CurrencyContext";
import appLogo from "../../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   ⭐ CATEGORY SOURCE — the SAME collection StoreProducts saves
   with. Products store `categoryId` (a CategoriesCollection id)
   + `attributes` (a record keyed by attribute id), so this page
   filters/labels by walking the same tree back up.
═══════════════════════════════════════════════════════════ */
import {
  DEPARTMENTS,
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

/* Rate types live with the fee maths so the shelf and the till agree. */
import {
  type RateType,
  DEFAULT_RATE_TYPE,
  calculateLineTotal,
} from "../../../redux/storeProductServiceFeeSlice";

import {
  Search, ShoppingBag, X, ChevronRight, Plus, Minus,
  User, ShoppingCart, MapPin, Globe,
  ChevronDown, LogOut,
  Package, Home, ShoppingBasket, LayoutGrid, Check, AlertTriangle,
  Tag, Sparkles,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",

  dark: "#1B0E06",
  black: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  border: "#EEDFC4",
  divider: "#F3E6CE",
};

const T = {
  pageBg: HEART.bg,
  cardBg: HEART.panelBg,
  black: HEART.dark,
  white: HEART.panelBg,
  offwhite: HEART.dark,
  muted: HEART.textMuted,
  mutedDim: HEART.textFaint,
  red: HEART.red,
  redDim: HEART.redSoft,
  border: HEART.border,
  grad: `linear-gradient(135deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  gradH: `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  outline: `1.5px solid ${HEART.border}`,
};

const GRAD_H = T.gradH;
const RGBA_RED = (a: number) => `rgba(200,32,46,${a})`;

/* Accent comes straight from the product's brand — never a random tint,
   never a per-department palette. One house colour, or the seller's own. */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || HEART.red;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS
   CategoriesCollection ids are hierarchical strings, but the
   deeper levels (PT-…, PRD-…) break the prefix scheme — so we
   ALWAYS resolve ancestry by walking parentId, never substrings.
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

/** ancestors + self, memoized (this runs per product per render otherwise) */
const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | null): string[] => {
  if (!id) return [];
  const hit = chainCache.get(id);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(id);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(id, chain);
  return chain;
};

const catName = (id?: string | null) => (id ? CAT_MAP.get(id)?.name ?? "" : "");
const catPathLabel = (id?: string | null) =>
  chainIdsOf(id).map((cid) => catName(cid)).join(" › ");

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTE DEFS
   Collection ids are 2-digit per level ("0801"); the attribute
   tree is 3-digit ("00801"). Walk up trying both widths — the
   same bridge the seller screen uses, so labels always match.
═══════════════════════════════════════════════════════════ */
const attrDefCache = new Map<string, any[]>();
const attrDefsFor = (categoryId?: string | null): any[] => {
  if (!categoryId) return [];
  const hit = attrDefCache.get(categoryId);
  if (hit) return hit;
  const keys = [...chainIdsOf(categoryId)].reverse().flatMap((cid) => [`0${cid}`, cid]);
  let found: any[] = [];
  for (const key of keys) {
    try {
      const defs = attributesForCategory(key as any);
      if (defs && defs.length) { found = defs; break; }
    } catch {
      /* unknown key — keep walking up */
    }
  }
  attrDefCache.set(categoryId, found);
  return found;
};

/** Resolve a saved attribute value (option id, array of ids, or free
    text) into a human label using the attribute definition. */
const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  if (Array.isArray(value) && value.length === 0) return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).filter(Boolean).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isColorAttr = (a: any) =>
  a?.inputType === "color" || /colou?r/i.test(String(a?.name ?? ""));

/**
 * What the shopper actually sees under "Details": only attributes this
 * product has a saved value for. Nothing is invented from the category
 * definition — an empty field is an empty field, not a blank row.
 * Colour is excluded because the variant picker already covers it.
 */
const savedAttrChips = (item: any): { name: string; value: string }[] => {
  const saved = item?.attributes ?? {};
  if (!saved || typeof saved !== "object") return [];
  return attrDefsFor(item?.categoryId)
    .filter((a: any) => !isColorAttr(a))
    .map((a: any) => ({ name: a.name, value: resolveAttrValue(a, saved[String(a.id)]) }))
    .filter((c) => !!c.value);
};

/* ═══════════════════════════════════════════════════════════
   VARIANTS — read straight off the saved product.
   If the seller didn't create it, it isn't selectable. There is
   no fallback to the category's option list: showing a colour
   nobody stocked is how you sell something you can't ship.
═══════════════════════════════════════════════════════════ */
export interface SavedVariant {
  colorId: string;
  name: string;
  hex?: string | null;
  imageUrl?: string;
  stock: number;
  rate: number | null;
  sku?: string;
}

const variantsOf = (item: any): SavedVariant[] => {
  const raw = item?.variants;
  if (!raw || typeof raw !== "object") return [];
  return Object.entries(raw)
    .map(([key, v]: [string, any]) => ({
      colorId: String(v?.colorId ?? key),
      name: String(v?.name ?? "Colour"),
      hex: v?.hex ?? null,
      imageUrl: v?.imageUrl ?? "",
      stock: Number(v?.stock) || 0,
      rate: v?.rate === null || v?.rate === undefined ? null : Number(v.rate),
      sku: v?.sku ?? "",
    }))
    .filter((v) => !!v.colorId);
};

const hasVariants = (item: any) => variantsOf(item).length > 0;

const rateTypeOf = (item: any): RateType =>
  (item?.rateType as RateType) || DEFAULT_RATE_TYPE;

const RATE_LABEL: Record<RateType, string> = {
  perItem: "/ item",
  perProduct: "/ product",
};

/** The price one unit of this line actually costs. */
const unitRateFor = (item: any, variant?: SavedVariant | null): number => {
  if (rateTypeOf(item) === "perItem") {
    if (variant?.rate != null) return variant.rate;
    if (item?.rate != null) return item.rate;
    /* per-item listings keep no price on the doc — fall back to the cheapest colour */
    const rates = variantsOf(item).map((v) => v.rate).filter((r): r is number => r != null);
    return rates.length ? Math.min(...rates) : 0;
  }
  return item?.rate ?? 0;
};

/** Cheapest / dearest across colours, for the "from €x" on a card. */
const priceRangeOf = (item: any): { min: number; max: number } => {
  const vs = variantsOf(item);
  if (rateTypeOf(item) !== "perItem" || vs.length === 0) {
    const r = item?.rate ?? 0;
    return { min: r, max: r };
  }
  const rates = vs.map((v) => (v.rate != null ? v.rate : item?.rate ?? 0));
  return { min: Math.min(...rates), max: Math.max(...rates) };
};

/** Stock is the sum of the colours when there are colours. */
const stockOf = (item: any): number => {
  const vs = variantsOf(item);
  if (vs.length) return vs.reduce((s, v) => s + (v.stock || 0), 0);
  return item?.stock ?? 0;
};

const inStockVariants = (item: any) => variantsOf(item).filter((v) => v.stock > 0);

const isOutOfStock = (item: any) =>
  item?.available === false || stockOf(item) <= 0;

/* ═══════════════════════════════════════════════════════════
   LOCATION
═══════════════════════════════════════════════════════════ */
type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";

const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};

/* tier 0 = same city, 1 = same country, 2 = same continent, 3 = world.
   LOCAL → 0-1, GLOBAL → 2-3 (disjoint sets, honest empty states) */
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* Departments with their own dedicated storefront — never shown here. */
const HIDDEN_DEPT_IDS = new Set([
 "08", // Food 
]);
const SHOP_DEPARTMENTS = DEPARTMENTS.filter((d) => !HIDDEN_DEPT_IDS.has(d.id));

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800;900&family=Archivo+Black&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button { font-family: inherit; }
input::placeholder { color: rgba(27,14,6,.35); }

@keyframes gradSpin { 0%{background-position:0% 50%;} 50%{background-position:100% 50%;} 100%{background-position:0% 50%;} }
@keyframes titleGlow  { 0%,100%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 14px 30px rgba(200,32,46,.18);} 50%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 18px 40px rgba(200,32,46,.30);} }
@keyframes svh-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
@keyframes ovalSheen {
  0%   { transform: translateX(-160%) skewX(-18deg); }
  60%,100% { transform: translateX(320%) skewX(-18deg); }
}

@media (prefers-reduced-motion: reduce){
  .svh-title, .svh-oval::after { animation: none !important; }
}

.p-card { transition: box-shadow .22s, transform .22s; }
.p-card:hover { box-shadow: 0 4px 10px rgba(0,0,0,.06), 0 24px 48px rgba(0,0,0,.16); transform: translateY(-6px); }
.sh-plus { transition: transform .15s; }
.sh-plus:hover  { transform: scale(1.08); }
.sh-plus:active { transform: scale(.92); }

.tab-item { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:${T.mutedDim}; padding:4px 10px; transition:color .2s; }
.tab-item.active { color:${T.red}; }

.svh-title {
  font-family: 'Archivo Black','Montserrat',sans-serif;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  line-height: 0.95;
  background: linear-gradient(180deg, ${HEART.dark} 0%, ${HEART.dark} 60%, ${HEART.red} 130%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: titleGlow 3.4s ease-in-out infinite;
}

.svh-hbtn{display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.16);
         border:1px solid rgba(255,255,255,.3);cursor:pointer;color:#fff;
         font-size:12px;font-weight:700;padding:9px 14px;border-radius:999px;
         flex-shrink:0;white-space:nowrap;transition:background .2s;font-family:inherit;}
.svh-hbtn:hover{background:rgba(255,255,255,.26);}

/* ══════════════════════════════════════════════
   ✨ THE OVAL — one continuous glassy capsule
   holding every department toggle.
══════════════════════════════════════════════ */
.svh-oval {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 10px;
  border-radius: 999px;
  background:
    linear-gradient(#FFFDF8, #FFF6E6) padding-box,
    linear-gradient(120deg, ${HEART.gold}, ${HEART.red} 45%, ${HEART.gold} 90%) border-box;
  border: 2px solid transparent;
  box-shadow:
    0 10px 30px rgba(200,32,46,.14),
    0 2px 6px rgba(27,14,6,.08),
    inset 0 1px 0 rgba(255,255,255,.9);
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.svh-oval::after {
  content: "";
  position: absolute;
  top: 0; bottom: 0; left: 0;
  width: 90px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.75), transparent);
  animation: ovalSheen 5.5s ease-in-out infinite;
  pointer-events: none;
}
.svh-oval-toggle {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 9px 16px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .03em;
  color: ${HEART.textMuted};
  background: transparent;
  transition: color .18s, transform .18s;
  white-space: nowrap;
}
.svh-oval-toggle:hover { color: ${HEART.dark}; transform: translateY(-1px); }
.svh-oval-toggle .svh-oval-ic { display: flex; font-size: 13px; color: ${HEART.goldDark}; transition: transform .18s; }
.svh-oval-toggle:hover .svh-oval-ic { transform: rotate(-8deg) scale(1.12); }
.svh-oval-toggle.active {
  color: #fff;
  background: linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
  box-shadow: 0 8px 18px rgba(200,32,46,.42), inset 0 1px 0 rgba(255,255,255,.28);
}
.svh-oval-toggle.active .svh-oval-ic { color: #fff; transform: none; }

.svh-scg-tile{background:none;border:none;cursor:pointer;padding:0;border-radius:14px;overflow:hidden;}

/* ── colour spheres, same object-like read as the seller side ── */
.svh-sphere-btn{background:none;border:none;padding:0;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:6px;width:64px;}
.svh-sphere{width:42px;height:42px;border-radius:50%;position:relative;transition:transform .18s;}
.svh-sphere-btn:hover .svh-sphere{transform:scale(1.1) translateY(-2px);}
.svh-sphere.sel{outline:2.5px solid ${HEART.red};outline-offset:3px;}
.svh-sphere-btn.oos{cursor:not-allowed;opacity:.4;}
.svh-sphere-btn.oos .svh-sphere{filter:grayscale(1);}
.svh-sphere-btn.oos:hover .svh-sphere{transform:none;}
.svh-sphere-name{font-size:10px;font-weight:600;color:${HEART.textMuted};text-align:center;line-height:1.2;word-break:break-word;}
.svh-sphere-btn.sel .svh-sphere-name{color:${HEART.redDark};font-weight:800;}
.svh-sphere-tick{position:absolute;bottom:-2px;right:-2px;width:16px;height:16px;border-radius:50%;background:${HEART.red};display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.3);}

.nav-item { display:flex; align-items:center; gap:12px; padding:13px 18px; cursor:pointer; font-size:14px; font-weight:700; color:${T.offwhite}; background:none; width:100%; text-align:left; border:none; border-bottom:1px solid ${HEART.divider}; transition:background .15s; }
.nav-item:hover { background: ${HEART.goldSoft}; }
`;

/* ═══════════════════════════════════════════════════════════
   SIDEBAR / PAGE STYLES
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard: { borderRadius: 18, padding: 18, background: "#fff", marginBottom: 16, boxShadow: "0 2px 16px rgba(27,14,6,.06)", border: T.outline },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.black },
  category: { padding: 10, borderRadius: 12, background: HEART.bg, marginTop: 8, border: `1px solid ${HEART.border}`, cursor: "pointer" },
  row: { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black },
  sub: { paddingLeft: 20, paddingTop: 8 },
  sidebar: { background: "rgba(255,255,255,.98)", borderRadius: 20, padding: 20, boxShadow: "0 4px 24px rgba(27,14,6,.08)", position: "sticky" as const, top: 116, maxHeight: "calc(100vh - 130px)", overflowY: "auto" as const, border: T.outline },
};

const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: T.pageBg, paddingBottom: 90, fontFamily: "'Montserrat',sans-serif", position: "relative" as const },
  content: { position: "relative" as const, zIndex: 1 },
  headerTop: { background: GRAD_H, backgroundSize: "200% 100%", position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_RED(0.25)}` },
  searchWrap: { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.18)", borderRadius: 999, overflow: "hidden", border: `1.5px solid rgba(255,255,255,.32)` },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn: { width: 42, minHeight: 40, background: "rgba(255,255,255,.16)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 20 },
  mobileGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  mobileTabs: { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: T.cardBg, display: "flex", justifyContent: "space-around", alignItems: "center", borderTop: `1px solid ${HEART.border}`, zIndex: 100 },
};

/* 3D sphere fill — lit above, shadowed below, so a colour reads as an object */
const sphere3D = (hex: string) =>
  `radial-gradient(circle at 35% 32%, rgba(255,255,255,.55) 0%, rgba(255,255,255,.10) 28%, transparent 52%),` +
  `radial-gradient(circle at 68% 72%, rgba(0,0,0,.30) 0%, transparent 55%),` +
  `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`;

const LIGHT_HEX = new Set([
  "#FFFFFF", "#FFFFF0", "#FFFDD0", "#FAFAFA", "#E6E6FA", "#F5F5DC", "#F8F8F0",
  "#87CEEB", "#98FF98", "#CDDC39", "#FFEB3B", "#FFC107", "#FFDB58", "#EAE0C8",
]);

/* ═══════════════════════════════════════════════════════════
   PAGE WATERMARK
═══════════════════════════════════════════════════════════ */
const PageWatermark: React.FC = () => (
  <div
    aria-hidden="true"
    style={{
      position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
      backgroundImage: `url(${appLogo})`, backgroundRepeat: "repeat",
      backgroundSize: "170px 170px", opacity: 0.045, filter: "grayscale(1)",
    }}
  />
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const StoreProductsHome: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const filteredListings = useSelector((s: RootState) => s.storeProducts.filteredProducts);
  const cartItems = useSelector((s: RootState) => (s as any).storeCart?.items ?? []);
  const ratingSummaries = useSelector(
    (s: RootState) => (s as any).storeProductRatings?.summaries ?? {}
  );

  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);
  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  /** Money in the shopper's currency. */
  const money = (v: number) => `${symbol ?? "€"}${((v ?? 0) * (rate ?? 1)).toFixed(2)}`;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  /* ── active department (the master toggle) ── */
  const [activeDept, setActiveDept] = useState<string>(
    SHOP_DEPARTMENTS.find((d) => !HIDDEN_DEPT_IDS.has(d.id))?.id ?? "00"
  );
  const dept = useMemo(
    () => SHOP_DEPARTMENTS.find((d) => d.id === activeDept) ?? SHOP_DEPARTMENTS[0],
    [activeDept]
  );

  /* any-level filter selections inside the active department */
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);

  /* sidebar drill state */
  const [openDeptPanel, setOpenDeptPanel] = useState<string | null>(SHOP_DEPARTMENTS[0]?.id ?? null);
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [qty, setQty] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [chosenVariantId, setChosenVariantId] = useState<string | null>(null);

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  /* ── brand distance tiers ── */
  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners() as any); }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId) as any);
    return () => { dispatch(stopUserNotificationsListener() as any); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode(
              { location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
              (results: any, status: string) => {
                if (status === "OK" && results?.[0]) {
                  const comps = results[0].address_components;
                  const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                  setUserCity(get("locality") || get("administrative_area_level_2"));
                  const country = get("country"); setUserCountry(country);
                  if (country) dispatch(filterLocalProductsByCountry(country) as any);
                }
              }
            );
          }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then((r) => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveCat(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const resetAllFilters = () => {
    setActiveCat(null); setActiveDeep(null); setActiveSort("default");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleDeptSelect = (deptId: string) => {
    setActiveDept(deptId);
    setActiveCat(null); setActiveDeep(null);
    setOpenDeptPanel(deptId);
    setOpenNodes(new Set());
  };

  const handleCatSelect = (catId: string) => {
    setActiveCat((prev) => (prev === catId ? null : catId));
    setActiveDeep(null);
  };

  const handleDeepSelect = (id: string) =>
    setActiveDeep((prev) => (prev === id ? null : id));

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const getQty = (id: string) =>
    cartItems
      .filter((i: any) => i.listingId === id)
      .reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  //const SINGLE_BRAND_CART_MODE = true;
  const SINGLE_BRAND_CART_MODE = false;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  /* ── detail sheet ── */
  const openProductModal = (item: any) => {
    setDetailItem(item);
    setQty(1);
    /* pre-select when there's only one colour actually in stock — one less
       tap, and it can't be the wrong choice */
    const available = inStockVariants(item);
    setChosenVariantId(available.length === 1 ? available[0].colorId : null);
    setDetailOpen(true);
  };
  const closeProductModal = () => {
    setDetailOpen(false);
    setDetailItem(null);
    setChosenVariantId(null);
    setQty(1);
  };

  /**
   * One place builds the cart line, so the shelf, the sheet and the till
   * never disagree about what's being bought or what it costs.
   */
  const buildCartLine = (item: any, variant: SavedVariant | null, quantity: number) => {
    const rt = rateTypeOf(item);
    const unit = unitRateFor(item, variant);
    return {
      listingId: item.listingId,
      name: item.name,
      rate: unit,
      rateType: rt,
      qty: quantity,
      /* what this line comes to, by the same maths the fee slice runs */
      lineTotal: calculateLineTotal(unit, quantity, rt),
      photo: variant?.imageUrl || item.photos?.[0],
      hostId: item.hostId,
      brandId: item.brandId,
      sku: variant?.sku || item.sku || "",
      categoryId: item.categoryId ?? "",
      /* the exact colour leaving the shelf — null when the product has none */
      variantId: variant?.colorId ?? null,
      variantName: variant?.name ?? null,
      variantHex: variant?.hex ?? null,
      variantImage: variant?.imageUrl ?? null,
      /* saved attributes ride along so the order reads back without a lookup */
      attributes: item.attributes ?? {},
    };
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    /* anything with colours has to be configured — never guess for the shopper */
    if (hasVariants(item)) { openProductModal(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart(buildCartLine(item, null, 1) as any));
  };

  const handleDetailContinue = (toCheckout: boolean) => {
    if (!detailItem || isOutOfStock(detailItem)) return;
    const variants = variantsOf(detailItem);
    const variant = variants.find((v) => v.colorId === chosenVariantId) ?? null;
    if (variants.length > 0 && !variant) return; // must pick a colour
    if (variant && variant.stock <= 0) return;

    ensureCartBrandCompat(detailItem.brandId);
    dispatch(addToCart(buildCartLine(detailItem, variant, qty) as any));
    closeProductModal();
    if (toCheckout) goToCheckout();
  };

  const goToCheckout = () =>
    navigate(`/pay/${userId}`, { state: { items: cartItems } });

  const handleLogout = async () => {
    await dispatch(logout() as any);
    navigate("/AuthSwitch");
  };

  /* ═══════════════════════════════════════════════════════════
     FILTERING
  ═══════════════════════════════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings || []).filter((l: any) => {
      if (!l.isPublished) return false;
      if (isOutOfStock(l)) return false; // hide sold-out products entirely
      const chain = chainIdsOf(l.categoryId);
      if (chain.some((cid) => HIDDEN_DEPT_IDS.has(cid))) return false;
      if (!chain.includes(activeDept)) return false;
      if (activeCat && !chain.includes(activeCat)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${(l.tags ?? []).join(" ")} ${l.vendor ?? ""} ${l.sku ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const priceOf = (p: any) => priceRangeOf(p).min;
    if (activeSort === "price_asc") list = [...list].sort((a, b) => priceOf(a) - priceOf(b));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => priceOf(b) - priceOf(a));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "newest") list = [...list].sort((a, b) => (Date.parse(b.createdAt ?? 0) || 0) - (Date.parse(a.createdAt ?? 0) || 0));

    return list;
  }, [filteredListings, activeDept, activeCat, activeDeep, searchQuery, activeSort]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));

    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    groups = [...groups].sort(
      (a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3)
    );
    return groups;
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleProductCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const activeFilterName = activeDeep ? catName(activeDeep) : activeCat ? catName(activeCat) : null;
  const deptCategories = useMemo(() => childrenOf(activeDept), [activeDept]);

  /* ═══════════════════════════════════
     HEADER
  ═══════════════════════════════════ */
  const HeaderBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "14px 20px" }}>
      {locationDenied ? (
        <button className="svh-hbtn" onClick={() => setLocationModalOpen(true)}><AlertTriangle size={14} /></button>
      ) : !userCountry ? (
        <div className="svh-hbtn">
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "svh-pulse 1.2s infinite" }} />
          <span style={{ display: isMobile ? "none" : "inline" }}>Locating…</span>
        </div>
      ) : (
        <button onClick={handleToggleGlobal} className="svh-hbtn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={S.searchWrap}>
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search the stores…"
          style={S.searchInput}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={13} />
          </button>
        )}
        <button style={S.searchBtn}><Search size={16} color="#fff" /></button>
      </div>

      <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="svh-hbtn">
        <LayoutGrid size={14} />
        <span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      <button onClick={goToCheckout} className="svh-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={16} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -6, right: -6, background: HEART.redDark, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>

      {!isMobile && (
        <div style={{ position: "relative" }}>
          <button onClick={() => setProfileMenuOpen((v) => !v)} className="svh-hbtn"><User size={15} /></button>
          <AnimatePresence>
            {profileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 200, background: "#fff", borderRadius: 16, border: T.outline, boxShadow: "0 16px 40px rgba(27,14,6,.2)", overflow: "hidden", zIndex: 400 }}
              >
                <button className="nav-item" onClick={() => navigate(`/profile/${userId}`)}><User size={15} /> Profile</button>
                <button className="nav-item" onClick={() => navigate(`/clientdashboard/${userId}`)}><ShoppingBag size={15} /> Orders</button>
                <button className="nav-item" style={{ color: T.red, borderBottom: "none" }} onClick={handleLogout}><LogOut size={15} /> Sign out</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  const DeptTitleBanner = () => (
    <div style={{ padding: isMobile ? "6px 4px 4px" : "4px 4px 8px", display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
      <span className="svh-title" style={{ fontSize: isMobile ? 30 : 46 }}>
        {dept?.name ?? "Shop"}
      </span>
      <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim, textTransform: "uppercase" }}>
        {locationDenied ? "Worldwide" : showGlobal ? "Global" : userCity ?? "Near You"}
      </span>
    </div>
  );

  const OvalToggleBar = () => (
    <div style={{ padding: isMobile ? "0 4px 14px" : "0 4px 18px" }}>
      <div className="svh-oval">
        <span style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0, padding: "0 6px 0 8px", color: HEART.goldDark }}>
          <Sparkles size={14} />
        </span>
        {SHOP_DEPARTMENTS.map((d) => (
          <button
            key={d.id}
            className={`svh-oval-toggle${d.id === activeDept ? " active" : ""}`}
            onClick={() => handleDeptSelect(d.id)}
          >
            <span className="svh-oval-ic">{d.icon}</span>
            {d.name}
          </button>
        ))}
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════════════════════
     SIDEBAR TREE
  ═══════════════════════════════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const isPicked = activeCat === node.id || activeDeep === node.id;

    return (
      <div style={depth === 0 ? A.category : { paddingLeft: 14, paddingTop: 7 }}>
        <div
          style={{ ...A.row, fontSize: depth === 0 ? 14 : 13, color: isPicked ? T.red : T.black }}
          onClick={() => {
            if (depth === 0) handleCatSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}
        >
          <span style={{ display: "flex", color: isPicked ? T.red : HEART.goldDark }}>{node.icon}</span>
          {node.name}
          {kids.length > 0 && (
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}>
              <ChevronRight size={13} color={T.mutedDim} />
            </motion.div>
          )}
          {isPicked && kids.length === 0 && <Check size={13} color={T.red} style={{ marginLeft: "auto" }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              <div style={A.sub}>
                {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const DeptCategoryTree: React.FC = () => {
    const isOpen = openDeptPanel === activeDept;
    return (
      <div style={A.commerceCard}>
        <div style={A.commerceHeader} onClick={() => setOpenDeptPanel(isOpen ? null : activeDept)}>
          {dept?.name}
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              {deptCategories.length === 0 ? (
                <p style={{ fontSize: 12, color: T.mutedDim, paddingTop: 10 }}>No sub-categories here yet.</p>
              ) : (
                deptCategories.map((cat) => <TreeNode key={cat.id} node={cat} depth={0} />)
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const CategoryPanelContent = () => (
    <>
      <OvalToggleBar />
      <DeptCategoryTree />
    </>
  );

  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.55)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, border: T.outline, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: HEART.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${HEART.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.red }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}><CategoryPanelContent /></div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     CATEGORY ICON BAR — house colours, not a palette per department
  ═══════════════════════════════════ */
  const CategoryIconBar = () => {
    if (!deptCategories.length) return null;
    return (
      <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: HEART.panelBg, borderRadius: 18, border: T.outline }}>
        <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
          {deptCategories.map((cat) => {
            const isSel = activeCat === cat.id;
            return (
              <button key={cat.id}
                onClick={() => handleCatSelect(cat.id)}
                style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
                <div style={{
                  width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: "50%", overflow: "hidden",
                  border: `2px solid ${isSel ? T.red : HEART.border}`,
                  boxShadow: isSel ? `0 0 0 3px ${T.redDim}` : "none", transition: "all .2s",
                }}>
                  {cat.image ? (
                    <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isSel ? 1 : 0.85, transition: "opacity .2s" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", background: isSel ? T.grad : HEART.goldSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: isSel ? "#fff" : HEART.goldDark, display: "flex", fontSize: isMobile ? 18 : 22 }}>{cat.icon}</span>
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: isSel ? 800 : 600, color: isSel ? T.red : T.black, whiteSpace: "nowrap" }}>{cat.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SHOP BY CATEGORY
  ═══════════════════════════════════ */
  const ShopByCategory = () => {
    if (!deptCategories.length) return null;

    const focusId = activeCat ?? deptCategories[0]?.id ?? activeDept;
    const tileSource = childrenOf(focusId);
    if (!tileSource.length) return null;

    const chunk4 = (arr: CategoryItem[]) => {
      const out: CategoryItem[][] = [];
      for (let i = 0; i < arr.length; i += 4) out.push(arr.slice(i, i + 4));
      return out;
    };
    const focusName = focusId === activeDept ? dept?.name : catName(focusId);
    const chunks = chunk4(tileSource);
    const cardGroups = chunks.map((tiles, idx) => ({
      title: chunks.length > 1 ? `${focusName} ${idx + 1}` : (focusName || "All"),
      tiles,
    }));

    return (
      <div style={{ marginBottom: 28 }}>
        <div onClick={() => setShopCatExpanded((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 14 }}>
          <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.black, letterSpacing: -0.3 }}>Shop by Category</span>
          <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}><ChevronDown size={20} color={T.muted} /></motion.div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div key={activeDept} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
              }>
                {cardGroups.map((group, gi) => (
                  <div key={`${group.title}-${gi}`}
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: "#fff", borderRadius: 20, overflow: "hidden", border: T.outline, boxShadow: "0 6px 28px rgba(27,14,6,.06)", transition: "box-shadow .22s, border-color .22s, transform .22s" }}
                    onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 8px 0 ${T.redDim}, 0 16px 38px rgba(27,14,6,.16)`; el.style.borderColor = T.red; el.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = "0 6px 28px rgba(27,14,6,.06)"; el.style.borderColor = HEART.border; el.style.transform = "translateY(0)"; }}
                  >
                    <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: T.black, letterSpacing: -0.2 }}>{group.title}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                      {group.tiles.map((node: CategoryItem) => {
                        const sel = activeCat === node.id || activeDeep === node.id;
                        const hasKids = childrenOf(node.id).length > 0;
                        return (
                          <button key={node.id} className="svh-scg-tile"
                            onClick={() => handleDeepSelect(node.id)}
                            style={{ padding: 0, display: "flex", flexDirection: "column" }}>
                            <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 14, overflow: "hidden", position: "relative", background: HEART.bg, outline: sel ? `2.5px solid ${T.red}` : "none", outlineOffset: 2, transition: "outline .15s" }}>
                              {node.image ? (
                                <>
                                  <img src={node.image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${HEART.dark}EE, transparent 55%)` }} />
                                  <span style={{ position: "absolute", bottom: isMobile ? 5 : 7, left: 4, right: 4, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.15, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>{node.name}</span>
                                </>
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: 4 }}>
                                  <span style={{ fontSize: isMobile ? 22 : 26, color: T.red, display: "flex" }}>{node.icon}</span>
                                  <span style={{ fontSize: isMobile ? 9 : 10.5, fontWeight: 800, color: T.black, textAlign: "center", lineHeight: 1.15 }}>{node.name}</span>
                                </div>
                              )}
                              {sel ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Check size={11} color="#fff" strokeWidth={3} />
                                </div>
                              ) : hasKids ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 16, height: 16, borderRadius: "50%", background: "rgba(255,255,255,.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <ChevronRight size={10} color={T.red} />
                                </div>
                              ) : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     BRAND ROW HEADER
  ═══════════════════════════════════ */
  const BrandRowHeader = ({ brandId }: { brandId: string }) => {
    const partner = brandMap.get(brandId);
    const shopName = partner?.name || "Boutique";
    const shopLogo = partner?.image || appLogo;
    const accent = brandAccentColor(partner);
    const goToStore = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!brandId || brandId === "unknown") return;
      navigate(`/brand/${brandId}`);
    };
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={goToStore}
          style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}`, background: "#fff", padding: 0, cursor: "pointer" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={goToStore}
          style={{ fontWeight: 900, fontSize: 14, color: T.black, textDecoration: "underline", textDecorationColor: accent, textDecorationThickness: 2, textUnderlineOffset: 4, cursor: "pointer" }}>
          {shopName}
        </span>
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={goToStore}
          style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "#fff", boxShadow: "0 3px 10px rgba(27,14,6,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={accent} />
        </motion.button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCT CARD
  ═══════════════════════════════════ */
  const ProductCard = ({ item }: { item: any }) => {
    const q = getQty(item.listingId);
    const summary = ratingSummaries[item.listingId];
    const accent = brandAccentColor(brandMap.get(item.brandId));
    const rt = rateTypeOf(item);
    const { min, max } = priceRangeOf(item);
    const variants = variantsOf(item);
    const compareAt = item.compareAtPrice ?? 0;
    const discount = compareAt > min && min > 0
      ? Math.round(((compareAt - min) / compareAt) * 100) : null;
    const cover = item.photos?.[0] || variants.find((v) => v.imageUrl)?.imageUrl;

    return (
      <div className="p-card" onClick={() => openProductModal(item)}
        style={{ background: T.cardBg, border: `1.5px solid ${T.border}`, borderRadius: 16, padding: 14, cursor: "pointer", position: "relative", display: "flex", flexDirection: "column" }}>
        {discount && (
          <span style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 6, letterSpacing: ".02em" }}>
            {discount}% OFF
          </span>
        )}
        <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: 12, overflow: "hidden", background: HEART.goldSoft, marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {cover
            ? <img src={cover} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; (e.currentTarget as HTMLImageElement).style.objectFit = "contain"; (e.currentTarget as HTMLImageElement).style.opacity = "0.4"; }} />
            : <Package size={32} color={T.red} style={{ opacity: .4 }} />}
        </div>

        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.black, lineHeight: 1.3, marginBottom: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 34 }}>
          {item.name}
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>
          {catName(item.categoryId) || item.category || "item"}
          {summary?.totalReviews ? ` · ${summary.avgRating.toFixed(1)}★ (${summary.totalReviews})` : ""}
        </div>

        {/* colours actually stocked — a swatch row, not a promise */}
        {variants.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 10 }}>
            {variants.slice(0, 5).map((v) => (
              <span key={v.colorId} title={v.name}
                style={{
                  width: 13, height: 13, borderRadius: "50%", flexShrink: 0,
                  border: "1px solid rgba(27,14,6,.14)",
                  background: v.hex ? sphere3D(v.hex) : HEART.goldSoft,
                  opacity: v.stock > 0 ? 1 : 0.3,
                }} />
            ))}
            {variants.length > 5 && (
              <span style={{ fontSize: 10, fontWeight: 700, color: T.mutedDim }}>+{variants.length - 5}</span>
            )}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto", gap: 8 }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: T.black }}>
              {max > min ? `${money(min)}+` : money(min)}
            </span>
            {rt === "perItem" && (
              <span style={{ fontSize: 10.5, fontWeight: 700, color: T.mutedDim, marginLeft: 4 }}>
                {RATE_LABEL.perItem}
              </span>
            )}
          </span>
          {q > 0 ? (
            <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, background: HEART.goldSoft, borderRadius: 999, padding: "3px 6px" }}>
              <button className="sh-plus" onClick={(e) => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }} style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: accent, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Minus size={12} />
              </button>
              <span style={{ fontSize: 12, fontWeight: 800, color: T.black, minWidth: 12, textAlign: "center" }}>{q}</span>
              <button className="sh-plus" onClick={(e) => handleAddToCart(item, e)} style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: accent, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Plus size={13} />
              </button>
            </div>
          ) : (
            <button className="sh-plus" onClick={(e) => handleAddToCart(item, e)}
              style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: `0 3px 10px ${accent}59`, flexShrink: 0 }}>
              <Plus size={16} color="#fff" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const SorryNoStores = ({ categoryName }: { categoryName: string }) => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>✦</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>Nothing in {categoryName} yet</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>Try another category, or switch department on the oval bar above.</p>
      <button onClick={resetAllFilters} style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>View All Categories</button>
    </div>
  );

  const NoDeptBrands = () => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>✦</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>No {dept?.name ?? "matching"} brands yet</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>Nobody's selling in this department yet — try another one on the oval bar above.</p>
      <button onClick={resetAllFilters} style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>View All Categories</button>
    </div>
  );

  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD_H }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: T.black }}>
            {activeFilterName ?? (locationDenied ? "All Products" : showGlobal ? "Global" : userCity ? `Near ${userCity}` : userCountry ?? "All Products")}
          </span>
          <span style={{ fontSize: 12, color: T.muted, background: HEART.goldSoft, padding: "2px 9px", borderRadius: 999 }}>{visibleProductCount}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select
            value={activeSort}
            onChange={(e) => setActiveSort(e.target.value as SortKey)}
            style={{ border: T.outline, borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: T.muted, background: "#fff", cursor: "pointer", outline: "none" }}
          >
            <option value="default">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price: low → high</option>
            <option value="price_desc">Price: high → low</option>
            <option value="name_asc">Name A–Z</option>
          </select>
          {locationActive && (
            <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? HEART.bg : T.redDim, borderRadius: 999, padding: "5px 13px", border: `1.5px solid ${showGlobal ? HEART.border : "rgba(200,32,46,.3)"}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.mutedDim : T.red, cursor: "pointer" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
            </button>
          )}
        </div>
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0 ? (
          activeFilterName ? <SorryNoStores categoryName={activeFilterName} /> : <NoDeptBrands />
        ) : locationDenied ? (
          <div style={{ textAlign: "center", padding: 56 }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>✦</div>
            <h3 style={{ fontWeight: 900, color: T.black, marginBottom: 8 }}>Location needed</h3>
            <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>Enable location to see local products.</p>
            <button onClick={() => setLocationModalOpen(true)}
              style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
              Enable Location
            </button>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 56 }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>✦</div>
            <h3 style={{ fontWeight: 900, color: T.black, marginBottom: 8 }}>
              {showGlobal ? `Nothing global in ${dept?.name ?? "this department"}.` : `Nothing local in ${dept?.name ?? "this department"}.`}
            </h3>
            <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>
              Try switching to {showGlobal ? "Local" : "Global"} above, or clear your filters.
            </p>
            <button onClick={resetAllFilters}
              style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
              View All Products
            </button>
          </div>
        )
      ) : (
        productsByBrand.map((group) => (
          <div key={group.brandId}>
            <BrandRowHeader brandId={group.brandId} />
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => <ProductCard key={item.listingId} item={item} />)}
            </div>
          </div>
        ))
      )}
    </div>
  );

  /* ═══════════════════════════════════
     PRODUCT DETAIL
     Colours come from the product's own saved variants — pick one
     and the photo, price, SKU and stock all follow it.
  ═══════════════════════════════════ */
  const DetailModal = () => {
    if (!detailOpen || !detailItem) return null;

    const partner = brandMap.get(detailItem?.brandId);
    const brandName = partner?.name;
    const accent = brandAccentColor(partner);

    const variants = variantsOf(detailItem);
    const chosen = variants.find((v) => v.colorId === chosenVariantId) ?? null;
    const rt = rateTypeOf(detailItem);

    const unit = unitRateFor(detailItem, chosen);
    const { min, max } = priceRangeOf(detailItem);
    const showRange = !chosen && max > min;

    /* stock follows the chosen colour; without colours it's the product's own */
    const stock = chosen ? chosen.stock : variants.length ? stockOf(detailItem) : (detailItem.stock ?? 0);
    const outOfStock = isOutOfStock(detailItem);

    const compareAt = detailItem.compareAtPrice ?? 0;
    const discount = compareAt > unit && unit > 0
      ? Math.round(((compareAt - unit) / compareAt) * 100) : null;

    const previewImg = chosen?.imageUrl || detailItem?.photos?.[0] || variants.find((v) => v.imageUrl)?.imageUrl;
    const chips = savedAttrChips(detailItem);

    const needsPick = variants.length > 0 && !chosen;
    const maxQty = Math.max(0, stock);
    const canContinue = !outOfStock && !needsPick && qty > 0 && qty <= maxQty;

    const CloseBtn = () => (
      <button onClick={closeProductModal} aria-label="Close"
        style={{ background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(27,14,6,.18)" }}>
        <X size={15} color={T.black} />
      </button>
    );

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 20, overflow: "hidden", maxWidth: isMobile ? "100%" : 460, width: "100%", maxHeight: isMobile ? "88vh" : "84vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: HEART.border, margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "20px 20px 0" }}>
          {previewImg ? (
            <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", borderRadius: 14, overflow: "hidden", marginBottom: 16, background: HEART.bg }}>
              <img src={previewImg} alt={detailItem?.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {discount && (
                <div style={{ position: "absolute", top: 10, left: 10, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999 }}>{discount}% OFF</div>
              )}
              <div style={{ position: "absolute", top: 10, right: 10 }}><CloseBtn /></div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}><CloseBtn /></div>
          )}

          {brandName && (
            <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", color: accent, textTransform: "uppercase", marginBottom: 4 }}>{brandName}</p>
          )}
          <div style={{ fontSize: 16, fontWeight: 800, color: T.black, marginBottom: 4 }}>{detailItem?.name}</div>

          {detailItem?.categoryId && (
            <p style={{ fontSize: 10.5, fontWeight: 700, color: T.mutedDim, marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
              <Tag size={10} /> {catPathLabel(detailItem.categoryId)}
            </p>
          )}

          {/* price — the unit it's charged by is stated, never assumed */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 22, fontWeight: 900, color: T.black }}>
              {showRange ? `${money(min)} – ${money(max)}` : money(unit)}
            </span>
            <span style={{ fontSize: 12, fontWeight: 800, color: accent }}>{RATE_LABEL[rt]}</span>
            {discount && !showRange && (
              <span style={{ fontSize: 13, color: T.mutedDim, textDecoration: "line-through" }}>{money(compareAt)}</span>
            )}
          </div>
          <p style={{ fontSize: 11, color: T.mutedDim, marginBottom: 18 }}>
            {rt === "perItem"
              ? "Priced by the item — the quantity you pick multiplies this."
              : "One price for the product, whatever quantity you take."}
          </p>

          {/* ── colours: exactly what the seller stocked ── */}
          {variants.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, textTransform: "uppercase" }}>Colour</span>
                {chosen ? (
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: T.black }}>{chosen.name}</span>
                ) : (
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: T.red }}>Please select</span>
                )}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {variants.map((v) => {
                  const sel = v.colorId === chosenVariantId;
                  const soldOut = v.stock <= 0;
                  const light = v.hex && LIGHT_HEX.has(v.hex.toUpperCase());
                  return (
                    <button
                      key={v.colorId}
                      className={`svh-sphere-btn${sel ? " sel" : ""}${soldOut ? " oos" : ""}`}
                      disabled={soldOut}
                      title={soldOut ? `${v.name} — sold out` : v.name}
                      onClick={() => { setChosenVariantId(v.colorId); setQty(1); }}
                    >
                      <span style={{ position: "relative", width: 42, height: 42 }}>
                        <span
                          className={`svh-sphere${sel ? " sel" : ""}`}
                          style={{
                            display: "block",
                            background: v.hex
                              ? sphere3D(v.hex)
                              : "conic-gradient(#e53935 0deg 51deg,#FF9800 51deg 102deg,#FFEB3B 102deg 153deg,#228B22 153deg 204deg,#4169E1 204deg 255deg,#9C27B0 255deg 306deg,#e53935 306deg 360deg)",
                            boxShadow: light
                              ? "inset -5px -4px 10px rgba(0,0,0,.12),inset 4px 4px 8px rgba(255,255,255,.6),0 1px 0 rgba(0,0,0,.15),0 4px 12px rgba(0,0,0,.12)"
                              : "inset -6px -5px 12px rgba(0,0,0,.30),inset 5px 5px 10px rgba(255,255,255,.18),0 5px 18px rgba(0,0,0,.20)",
                          }}
                        />
                        {sel && <span className="svh-sphere-tick"><Check size={10} color="#fff" strokeWidth={3} /></span>}
                      </span>
                      <span className="svh-sphere-name">
                        {v.name}
                        {rt === "perItem" && v.rate != null && (
                          <span style={{ display: "block", fontWeight: 800, color: soldOut ? T.mutedDim : accent }}>
                            {money(v.rate)}
                          </span>
                        )}
                        {soldOut && <span style={{ display: "block", color: T.mutedDim }}>Sold out</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
              {chosen?.sku && (
                <p style={{ fontSize: 10, color: T.mutedDim, marginTop: 10, fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace" }}>
                  SKU {chosen.sku}
                </p>
              )}
            </div>
          )}

          {/* ── details: only what this product actually has saved ── */}
          {chips.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, textTransform: "uppercase", marginBottom: 10 }}>Details</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {chips.map((c) => (
                  <div key={c.name} style={{ background: HEART.bg, border: `1px solid ${HEART.border}`, borderRadius: 12, padding: "9px 11px" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: T.mutedDim, marginBottom: 2 }}>{c.name}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.black }}>{c.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detailItem?.vendor && (
            <p style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>
              <strong style={{ color: T.black }}>Vendor:</strong> {detailItem.vendor}
            </p>
          )}
          {detailItem?.tags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {detailItem.tags.map((t: string) => (
                <span key={t} style={{ fontSize: 10, fontWeight: 700, color: T.muted, background: HEART.bg, border: `1px solid ${HEART.border}`, borderRadius: 999, padding: "3px 10px" }}>#{t}</span>
              ))}
            </div>
          )}

          {detailItem?.description && (
            <div
              style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, marginBottom: 18 }}
              dangerouslySetInnerHTML={{ __html: detailItem.description }}
            />
          )}

          {outOfStock ? (
            <p style={{ fontSize: 11, fontWeight: 700, color: T.mutedDim, marginBottom: 10 }}>Out of stock</p>
          ) : chosen && chosen.stock <= 5 ? (
            <p style={{ fontSize: 11, fontWeight: 700, color: T.red, marginBottom: 10 }}>Only {chosen.stock} left in {chosen.name}</p>
          ) : !variants.length && stock <= 5 ? (
            <p style={{ fontSize: 11, fontWeight: 700, color: T.red, marginBottom: 10 }}>Only {stock} left</p>
          ) : null}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim }}>QTY</span>
            <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 12, overflow: "hidden", background: HEART.bg, opacity: needsPick ? 0.5 : 1 }}>
              <button onClick={() => setQty((v) => Math.max(1, v - 1))} disabled={needsPick}
                style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: needsPick ? "not-allowed" : "pointer", fontSize: 16, color: T.red }}>−</button>
              <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.black, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
              <button onClick={() => setQty((v) => Math.min(maxQty || 1, v + 1))} disabled={needsPick || qty >= maxQty}
                style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: needsPick || qty >= maxQty ? "not-allowed" : "pointer", fontSize: 16, color: T.red, opacity: needsPick || qty >= maxQty ? 0.4 : 1 }}>+</button>
            </div>
            {/* what they'll actually be charged, by the same maths as the till */}
            {!needsPick && (
              <span style={{ marginLeft: "auto", textAlign: "right" }}>
                <span style={{ display: "block", fontSize: 9.5, fontWeight: 800, letterSpacing: ".1em", color: T.mutedDim, textTransform: "uppercase" }}>Total</span>
                <span style={{ fontSize: 16, fontWeight: 900, color: T.black }}>
                  {money(calculateLineTotal(unit, qty, rt))}
                </span>
              </span>
            )}
          </div>
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(false)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1.5px solid ${T.black}`, background: "transparent", color: T.black, fontWeight: 700, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed", opacity: canContinue ? 1 : 0.45 }}>
            {needsPick ? "Pick a colour" : "Add to Bag"}
          </button>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(true)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: "none", background: canContinue ? GRAD_H : "#C8C8C8", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Buy Now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={closeProductModal}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.7)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>{content}</motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 460, borderRadius: 20, overflow: "hidden", boxShadow: "0 32px 80px rgba(27,14,6,.35)" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.8)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={(e) => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: "#fff", boxShadow: "0 30px 80px rgba(27,14,6,.5)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: GRAD_H, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: T.black }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see local products.</p>
            <button onClick={() => setLocationModalOpen(false)} style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Got it</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />, label: "Home", active: true, action: () => navigate(`/StoreProductsHome/${userId}`) },
        { icon: <ShoppingBasket size={22} />, label: "Stores", active: false, action: () => navigate("/Stores") },
        {
          label: "Cart", active: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartItems.length > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>}
            </div>
          ), action: goToCheckout
        },
        { icon: <ShoppingBag size={22} />, label: "Orders", active: false, action: () => navigate(`/glamourclientdashboard/${userId}`) },
        { icon: <User size={22} />, label: "Profile", active: false, action: () => navigate(`/profile/${userId}`) },
      ].map((t) => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action} className={`tab-item${t.active ? " active" : ""}`} style={{ position: "relative" }}>
          {React.cloneElement(t.icon as any, { color: t.active ? T.red : T.mutedDim })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>
      <PageWatermark />

      <div style={S.content}>
        <header style={S.headerTop}>
          <HeaderBar />
        </header>

        <MobileCategorySheet />

        {!isMobile && (
          <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
            {desktopCatOpen && (
              <aside style={A.sidebar}>
                <DeptCategoryTree />
              </aside>
            )}
            <main style={{ minWidth: 0 }}>
              <DeptTitleBanner />
              <OvalToggleBar />
              <CategoryIconBar />
              <ShopByCategory />
              {renderProducts(false)}
            </main>
          </div>
        )}

        {isMobile && (
          <div style={{ padding: "14px 12px 0" }}>
            <DeptTitleBanner />
            <OvalToggleBar />
            <CategoryIconBar />
            <div style={{ marginTop: 16 }}><ShopByCategory /></div>
            {renderProducts(true)}
          </div>
        )}

        <DetailModal />
        <LocationModal />
        <BottomTabs />
      </div>
    </div>
  );
};

export default StoreProductsHome;